import React, { useEffect, useState } from 'react';
import InnerImageZoom from 'react-inner-image-zoom';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from './firebase';
import {
  doc,
  updateDoc,
  collection,
  setDoc,
  doc as firestoreDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import useReadableLocation from './useReadableLocation';
import { Dialog, DialogBody, Button, Input, Textarea, Chip } from "@material-tailwind/react";

export default function ViewThoughtModal({ thought, onClose, user }) {
  // ─────────────────────────────────────────────────────────────
  // 1) All hooks at the top level (unconditional)
  // ─────────────────────────────────────────────────────────────
  const [isDialogReady, setIsDialogReady] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(thought?.text || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editTags, setEditTags] = useState(thought?.tags?.join(', ') || '');

  // Always call hook; internally it should handle a missing location
  const address = useReadableLocation(thought?.location);

  useEffect(() => {
    // Dialog is ready if a thought is present
    setIsDialogReady(Boolean(thought));
  }, [thought]);

  // ─────────────────────────────────────────────────────────────
  // 2) Early returns after hooks
  // ─────────────────────────────────────────────────────────────
  if (!user) return null;
  if (!thought || !isDialogReady) return null;

  // ─── Updating the text ───────────────────
  const handleSave = async () => {
    if (!thought) return; // Guard clause
    setIsSaving(true);
    try {
      const thoughtRef = doc(db, 'thoughts', thought.id);
      await updateDoc(thoughtRef, {
        text: editText,
        editedAt: serverTimestamp()
      });
      thought.text = editText;
      thought.editedAt = new Date();
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating thought text:', err);
    }
    setIsSaving(false);
  };

  // ─── Updating the tags ──────────────────
  const handleTagUpdate = async () => {
    if (!thought) return; // Guard clause
    const tagArray = editTags
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);

    const thoughtRef = doc(db, 'thoughts', thought.id);
    await updateDoc(thoughtRef, {
      tags: tagArray,
      editedAt: serverTimestamp()
    });

    for (const tag of tagArray) {
      // Ensure thought.userId is available
      const tagRef = firestoreDoc(collection(db, 'users', thought.userId || user?.id, 'tags'), tag);
      const tagSnap = await getDoc(tagRef);
      if (!tagSnap.exists()) {
        await setDoc(tagRef, {
          name: tag,
          createdAt: serverTimestamp()
        });
      }
    }

    thought.tags = tagArray;
    setIsEditingTags(false);
  };

  const handleClose = () => {
    // Reset state based on the current thought prop, or defaults if thought is null (though caught by early return)
    setIsEditing(false);
    setIsEditingTags(false);
    setEditText(thought?.text || '');
    setEditTags(thought?.tags?.join(', ') || '');
    onClose();
  };

  return (
    <Dialog open={Boolean(thought)} handler={handleClose} size="xl">
      <DialogBody className="px-4 pt-4 pb-2 max-h-screen">
        <div className="flex justify-end mb-4">
          <Button
            variant="outlined"
            color="blue"
            onClick={handleClose}
            aria-label="Close"
            className="text-xl p-0 w-10 h-10 flex items-center justify-center"
          >
            &times;
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 max-h-[calc(100vh-6rem)]">
          {/* Left column: only image if present, otherwise text */}
          <div className="lg:w-2/3 w-full overflow-y-auto pr-2">
            {thought.imagePath ? (
              <ZoomedThoughtImage imagePath={thought.imagePath} />
            ) : (
              <>
                {isEditing ? (
                  <div className="mt-4">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      label="Edit Thought"
                      variant="outlined"
                      crossOrigin=""
                      rows={4}
                      className="mb-2 text-gray-900"
                      autoFocus
                    />
                    <div className="flex gap-2 mb-4">
                      <Button onClick={handleSave} color="blue" size="sm" disabled={isSaving}>
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        size="sm"
                        onClick={() => {
                          setIsEditing(false);
                          setEditText(thought.text || '');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p
                    className={`text-gray-900 text-lg whitespace-pre-wrap mb-4 ${
                      thought.text ? 'cursor-pointer hover:bg-gray-100' : 'italic text-gray-400 cursor-pointer'
                    }`}
                    onClick={() => {
                      setEditText(thought.text || '');
                      setIsEditing(true);
                    }}
                  >
                    {thought.text !== undefined && thought.text !== ''
                      ? thought.text
                      : 'Click to add text'}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Right column: Metadata */}
          <div className="lg:w-1/3 w-full overflow-y-auto">
            {/* If image present: show text block at top of right column */}
            {thought.imagePath && (
              isEditing ? (
                <div className="mt-4 mb-4">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    label="Edit Thought"
                    variant="outlined"
                    crossOrigin=""
                    rows={4}
                    className="mb-2 text-gray-900"
                    autoFocus
                  />
                  <div className="flex gap-2 mb-4">
                    <Button onClick={handleSave} color="blue" size="sm" disabled={isSaving}>
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditText(thought.text || '');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p
                  className={`text-gray-900 text-lg whitespace-pre-wrap mb-4 ${
                    thought.text ? 'cursor-pointer hover:bg-gray-100' : 'italic text-gray-400 cursor-pointer'
                  }`}
                  onClick={() => {
                    setEditText(thought.text || '');
                    setIsEditing(true);
                  }}
                >
                  {thought.text !== undefined && thought.text !== ''
                    ? thought.text
                    : 'Click to add text'}
                </p>
              )
            )}
            {!thought.imagePath && isEditing && <div className="mb-4" />}

            <div className="text-sm text-gray-600 space-y-1 mb-4">
              {thought.timestamp && (
                <p>{format(thought.timestamp.toDate(), 'MMMM d, yyyy \'at\' h:mm a')}</p>
              )}
              {(thought.imageSize || thought.thumbnailSize) && (
                <p>{(((thought.imageSize || 0) + (thought.thumbnailSize || 0)) / 1024).toFixed(1)} KB</p>
              )}
              {thought.displayName && <p>By {thought.displayName}</p>}
              {thought.location && (
                <p>📍 {address || `${thought.location.lat.toFixed(4)}, ${thought.location.lng.toFixed(4)}`}</p>
              )}
              {thought.editedAt && (
                <p className="italic">
                  Edited {formatDistanceToNow(
                    // If Firestore Timestamp, call toDate(), otherwise assume JS Date
                    thought.editedAt.toDate ? thought.editedAt.toDate() : thought.editedAt,
                    { addSuffix: true }
                  )}
                </p>
              )}
            </div>

            <div className="mb-4">
              {isEditingTags ? (
                <div className="flex flex-col gap-2">
                  <Input
                    label="Tags (comma-separated)"
                    value={editTags}
                    onChange={(e) => setEditTags(e.target.value)}
                    variant="standard"
                    crossOrigin=""
                  />
                  <div className="flex gap-2">
                    <Button color="blue" size="sm" onClick={handleTagUpdate}>Save</Button>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => {
                        setIsEditingTags(false);
                        setEditTags(thought.tags?.join(', ') || '');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setIsEditingTags(true)}
                  className={`flex flex-wrap gap-2 cursor-pointer ${thought.tags.length === 0 ? 'italic text-gray-500' : ''}`}
                >
                  {thought.tags.length === 0
                    ? 'Click to add tags'
                    : thought.tags.map((tag, i) => (
                        <Chip
                          key={i}
                          value={tag}
                          size="sm"
                          variant="ghost"
                          color="blue"
                          className="cursor-pointer"
                        />
                      ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// ─── ZoomedThoughtImage ─────────────────────────────────────────────
function ZoomedThoughtImage({ imagePath }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchImage = async () => {
      try {
        const imageRef = storageRef(storage, imagePath);
        const downloadUrl = await getDownloadURL(imageRef);
        if (isMounted) setUrl(downloadUrl);
      } catch (err) {
        console.error('Error loading image in modal:', err);
      }
    };
    fetchImage();
    return () => {
      isMounted = false;
    };
  }, [imagePath]);

  if (!url) return null;

  return (
    <div className="flex-1 flex items-center justify-center">
      <InnerImageZoom
        src={url}
        alt="Thought"
        zoomType="click"
        zoomPreload={true}
        moveType="drag"
        className="h-full object-contain"
      />
    </div>
  );
}